import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Component, Inject, Injectable, NgModule } from '@angular/core';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import * as i4 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { faTimesCircle, faInfoCircle, faCircleQuestion, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

var ModalButton;
(function (ModalButton) {
    ModalButton["Accept"] = "accept";
    ModalButton["Cancel"] = "cancel";
})(ModalButton || (ModalButton = {}));

class ModalComponent {
    constructor(activeModal) {
        this.activeModal = activeModal;
        this.accepted = new EventEmitter();
        this.canceled = new EventEmitter();
    }
    ngOnInit() {
        this.modal = this.modalConfig;
        this.acceptEnabled = this.modal.buttons.includes(ModalButton.Accept);
        this.cancelEnabled = this.modal.buttons.includes(ModalButton.Cancel);
    }
    accept() {
        this.accepted.emit();
        this.activeModal.close();
    }
    cancel() {
        this.canceled.emit();
        this.activeModal.close();
    }
    getColumnClass() {
        return this.acceptEnabled && this.cancelEnabled ? 'col-6' : 'col-12';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalComponent, deps: [{ token: i1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.3", type: ModalComponent, selector: "ng-component", inputs: { modalConfig: "modalConfig" }, outputs: { accepted: "accepted", canceled: "canceled" }, ngImport: i0, template: "<div class=\"modal-body py-4\">\n    <div class=\"row mb-0\">\n        <div class=\"col-md-2 text-center text-{{ modal.iconColor }} pt-1\">\n            <fa-icon [icon]=\"modal.icon\" class=\"\" size=\"2x\"></fa-icon>\n        </div>\n        <div class=\"col-md-10\" style=\"max-height: 50vh; overflow-y: auto\">\n            <h5>{{ modal.header | translate }}</h5>\n            <p class=\"align-text-middle mb-0\" [innerHTML]=\"modal.message | translate: modal.messageParams\"></p>\n        </div>\n    </div>\n</div>\n<div class=\"modal-footer\">\n    <div class=\"d-flex gap-2 col-12 px-4 py-3\">\n        @if (cancelEnabled) {\n            <a (click)=\"cancel()\" class=\"btn btn-dpred\" [ngClass]=\"getColumnClass()\">\n                {{ 'ext.modal.button.cancel' | translate }}\n            </a>\n        }\n        @if (acceptEnabled) {\n            <a (click)=\"accept()\" class=\"btn btn-dpprimary\" [ngClass]=\"getColumnClass()\">\n                {{ 'ext.modal.button.accept' | translate }}\n            </a>\n        }\n    </div>\n</div>\n", dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: i3.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ template: "<div class=\"modal-body py-4\">\n    <div class=\"row mb-0\">\n        <div class=\"col-md-2 text-center text-{{ modal.iconColor }} pt-1\">\n            <fa-icon [icon]=\"modal.icon\" class=\"\" size=\"2x\"></fa-icon>\n        </div>\n        <div class=\"col-md-10\" style=\"max-height: 50vh; overflow-y: auto\">\n            <h5>{{ modal.header | translate }}</h5>\n            <p class=\"align-text-middle mb-0\" [innerHTML]=\"modal.message | translate: modal.messageParams\"></p>\n        </div>\n    </div>\n</div>\n<div class=\"modal-footer\">\n    <div class=\"d-flex gap-2 col-12 px-4 py-3\">\n        @if (cancelEnabled) {\n            <a (click)=\"cancel()\" class=\"btn btn-dpred\" [ngClass]=\"getColumnClass()\">\n                {{ 'ext.modal.button.cancel' | translate }}\n            </a>\n        }\n        @if (acceptEnabled) {\n            <a (click)=\"accept()\" class=\"btn btn-dpprimary\" [ngClass]=\"getColumnClass()\">\n                {{ 'ext.modal.button.accept' | translate }}\n            </a>\n        }\n    </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i1.NgbActiveModal }], propDecorators: { modalConfig: [{
                type: Input
            }], accepted: [{
                type: Output
            }], canceled: [{
                type: Output
            }] } });

var ModalType;
(function (ModalType) {
    ModalType["Error"] = "error";
    ModalType["Info"] = "info";
    ModalType["Question"] = "question";
    ModalType["Warning"] = "warning";
})(ModalType || (ModalType = {}));

class Modal {
    constructor(type, header, icon, iconColor, message, buttons, messageParams) {
        this.type = type;
        this.header = header;
        this.icon = icon;
        this.iconColor = iconColor;
        this.message = message;
        this.buttons = buttons;
        this.messageParams = messageParams;
    }
}

class ModalError extends Modal {
    constructor(header, message, messageParams) {
        super(ModalType.Error, header, faTimesCircle, 'dpred', message, [ModalButton.Accept], messageParams);
    }
}

class ModalInfo extends Modal {
    constructor(header, message, messageParams) {
        super(ModalType.Info, header, faInfoCircle, 'info', message, [ModalButton.Accept], messageParams);
    }
}

class ModalQuestion extends Modal {
    constructor(header, message, messageParams) {
        super(ModalType.Question, header, faCircleQuestion, 'info', message, [ModalButton.Accept, ModalButton.Cancel], messageParams);
    }
}

class ModalWarning extends Modal {
    constructor(header, message, messageParams) {
        super(ModalType.Warning, header, faTriangleExclamation, 'warning', message, [ModalButton.Accept], messageParams);
    }
}

class ModalService {
    constructor(modal, appName) {
        this.modal = modal;
        this.appName = appName;
    }
    openError(message, messageParams, header = this.appName) {
        const modalConfig = new ModalError(header, message, messageParams);
        return this.openModal(modalConfig, this.modal);
    }
    openInfo(message, messageParams, header = this.appName) {
        const modalConfig = new ModalInfo(header, message, messageParams);
        return this.openModal(modalConfig, this.modal);
    }
    openQuestion(message, messageParams, header = this.appName) {
        const modalConfig = new ModalQuestion(header, message, messageParams);
        return this.openModal(modalConfig, this.modal);
    }
    openWarning(message, messageParams, header = this.appName) {
        const modalConfig = new ModalWarning(header, message, messageParams);
        return this.openModal(modalConfig, this.modal);
    }
    openModal(modalConfig, modal) {
        const modalReference = modal.open(ModalComponent, { backdrop: 'static', keyboard: false });
        modalReference.componentInstance.modalConfig = modalConfig;
        return modalReference;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalService, deps: [{ token: i1.NgbModal }, { token: 'appName' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.NgbModal }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['appName']
                }] }] });

class ModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.3", ngImport: i0, type: ModalModule, declarations: [ModalComponent], imports: [CommonModule,
            FontAwesomeModule,
            NgbModalModule,
            TranslateModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalModule, imports: [CommonModule,
            FontAwesomeModule,
            NgbModalModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ModalComponent
                    ],
                    imports: [
                        CommonModule,
                        FontAwesomeModule,
                        NgbModalModule,
                        TranslateModule,
                    ],
                    exports: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ModalModule as DpModalModule, ModalService as DpModalService };
